<template>
    <b-modal
        id="report-photos-upload-modal"
        v-model="visible"
        title="Загрузка фотографий по отчёту"
        centered
        size="lg"
        scrollable
        :no-close-on-backdrop="busy"
        :no-close-on-esc="busy"
        :hide-header-close="busy"
        @hide="onHide"
        @hidden="reset"
    >
        <div class="report-photo-upload-body">
            <p class="text-warning small mb-2">
                <i class="fas fa-exclamation-triangle mr-1"></i>
                Загруженные к этому отчёту фотографии будут автоматически отправлены в Telegram-группу объекта.
            </p>

            <div v-if="report" class="report-photo-upload-meta">
                <strong>Отчёт №{{ report.shift_id }}</strong>
                <span v-if="report.object && report.object.name">{{ report.object.name }}</span>
            </div>

            <b-alert v-if="error" show variant="danger">
                {{ error }}
            </b-alert>

            <PhotoUploadDevice
                :key="uploadDeviceKey"
                v-model="files"
                @status-change="onUploadStatusChange"
            />
        </div>

        <template #modal-footer>
            <b-button
                variant="secondary"
                :disabled="busy"
                @click="close"
            >
                Закрыть
            </b-button>
            <b-button
                variant="primary"
                :disabled="!files.length || !allFilesProcessed || busy"
                @click="submit"
            >
                {{ busy ? 'Загрузка...' : 'Загрузить' }}
            </b-button>
        </template>
    </b-modal>
</template>

<script>
import PhotoUploadDevice from '@/components/PhotoUploadDevice/PhotoUploadDevice.vue';
import {notifyInstallPromotionSuccess} from '@/utils/installPromotion';

export default {
    name: 'ReportPhotoUploadModal',
    components: {PhotoUploadDevice},
    props: {
        value: {
            type: Boolean,
            default: false
        },
        report: {
            type: Object,
            default: null
        }
    },
    data() {
        return {
            files: [],
            busy: false,
            error: '',
            allFilesProcessed: false,
            resetVersion: 0
        };
    },
    computed: {
        visible: {
            get() {
                return this.value;
            },
            set(value) {
                this.$emit('input', value);
            }
        },
        photoUploadResetVersion() {
            return this.$store.state.lifecycle && this.$store.state.lifecycle.photoUploadResetVersion
                ? this.$store.state.lifecycle.photoUploadResetVersion
                : 0;
        },
        uploadDeviceKey() {
            return `${this.photoUploadResetVersion}_${this.resetVersion}`;
        }
    },
    watch: {
        files: {
            handler(value) {
                const ids = Array.isArray(value)
                    ? value.map(item => item && item.id).filter(Boolean)
                    : [];
                this.$store.dispatch('lifecycle_setTempPhotoIdsForCleanup', ids).catch(() => {});
            },
            immediate: true
        },
        photoUploadResetVersion() {
            if (this.visible) {
                this.visible = false;
                this.clearState();
            }
        }
    },
    methods: {
        clearState() {
            this.resetVersion += 1;
            this.files = [];
            this.busy = false;
            this.error = '';
            this.allFilesProcessed = false;
            this.$store.commit('shiftReport_setUploadProgress', {done: 0, total: 0});
            this.$store.dispatch('lifecycle_setTempPhotoIdsForCleanup', []).catch(() => {});
            this.$store.dispatch('lifecycle_setPhotoDeviceStatus', {
                total: 0,
                hasUploading: false,
                errorCount: 0,
                allProcessed: false
            }).catch(() => {});
        },
        reset() {
            if (this.busy) return;
            this.clearState();
        },
        onHide(event) {
            if (this.busy && event && typeof event.preventDefault === 'function') {
                event.preventDefault();
            }
        },
        close() {
            if (this.busy) return;
            this.visible = false;
        },
        onUploadStatusChange(payload) {
            this.allFilesProcessed = !!(payload && payload.allProcessed);
        },
        async submit() {
            if (!this.report || !this.report.shift_id) {
                this.error = 'Отчёт не выбран';
                return;
            }
            if (!this.files.length) {
                this.error = 'Выберите фото для догрузки';
                return;
            }
            if (!this.allFilesProcessed) {
                this.error = 'Дождитесь завершения подготовки файлов';
                return;
            }

            this.busy = true;
            this.error = '';
            try {
                const result = await this.$store.dispatch('reports/commitTempPhotos', {
                    id: this.report.shift_id,
                    photos: this.files
                });
                if (result && Number(result.attachedCount) > 0) {
                    notifyInstallPromotionSuccess({source: 'report-photos-committed'});
                }
                this.$emit('uploaded', {
                    report: this.report,
                    uploadedCount: result && result.attachedCount ? result.attachedCount : this.files.length
                });
                this.visible = false;
            } catch (err) {
                this.error = err && err.response && err.response.data && (err.response.data.msg || err.response.data.message)
                    ? (err.response.data.msg || err.response.data.message)
                    : (err && err.message ? err.message : 'Не удалось догрузить фото');
            } finally {
                this.busy = false;
            }
        }
    }
};
</script>

<style scoped>
.report-photo-upload-body {
    padding-bottom: 8px;
    position: relative;
}

.report-photo-upload-meta {
    align-items: center;
    color: #6c757d;
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-bottom: 12px;
}

.report-photo-upload-meta strong {
    color: #212529;
}

.report-photo-upload-meta span {
    min-width: 0;
    overflow-wrap: anywhere;
}
</style>
